import { IContextMenuDefinition } from "Grids/Interfaces";
import { Control, CONTROL_ID_ATTRIBUTE_NAME } from "Controls/Control";
import { DisplayModes, CssClass } from "Enums";
import { ControlTemplate } from "Controls/Entities";
import { Navigation, Util } from "Core";
import { IDataRow } from "Entities";
import { ControlEventTypes } from "Core/Events/EventTypes";

/**
 * ## ContextMenuControl
 * 
 * Control to render a contextmenu on a link.
 * @since 4.0.0
 * @example 
 * ```JavaScript
 * // Access the instance of a ContextMenuControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 * 
 * // On page:
 * $dms(1337)
 * ```
 */
export class ContextMenuControl extends Control {

    private _kendoElement: kendo.ui.ContextMenu;
    private popup: JQuery = $("<ul>");

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-contextmenu";

    /**
     * @hidden
     */
    protected contextMenuDefinitions: IContextMenuDefinition[];

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.contextMenuDefinitions = this.controlDefinition.ContextMenuDefinitions;

        let options: kendo.ui.ContextMenuOptions = {
            target: this.jQueryElement,
            alignToAnchor: true,
            showOn: "click",
            select: (e) => this.onSelect(e),
            open: (e) => this.onOpen(e)
        };

        $.extend(options, {
            copyAnchorStyles: false // todo: check future typescript definitions.
        });

        this._kendoElement = new kendo.ui.ContextMenu(this.popup[0], options);
    }

    /**
     * Triggers when ContextMenu is about to open, content is created
     * @param e EventArgs. 
     */
    private onOpen(e: kendo.ui.ContextMenuOpenEvent) {
        let dataSourceObjects = [];
        let dataRow: IDataRow = this.execute(ControlEventTypes.GetDataRow, null);
        for (let i = 0, len = this.contextMenuDefinitions.length; i < len; i++) {
            let contextMenuDefinition = this.contextMenuDefinitions[i];
            if ((contextMenuDefinition.RangeColumnName && Util.isValueInRange(dataRow[contextMenuDefinition.RangeColumnName], contextMenuDefinition.RangeFrom, contextMenuDefinition.RangeTo))
                || !contextMenuDefinition.RangeColumnName) {
                let sourceObject = {
                    text: this.textBlockService.get(contextMenuDefinition.Caption, this.textBlockCategory),
                    attr: {
                        "dms-contextMenuLinkId": contextMenuDefinition.ContextMenuID
                    },
                    encoded: false
                };
                dataSourceObjects.push(sourceObject);
            }
        }
        this._kendoElement.setOptions({ dataSource: dataSourceObjects });
    }

    /**
     * Triggers when ContextMenuItem is clicked
     * @param e 
     */
    private onSelect(e: kendo.ui.ContextMenuSelectEvent) {
        let id = $(e.item).attr("dms-contextMenuLinkId");
        for (let i = 0, len = this.contextMenuDefinitions.length; i < len; i++) {
            let cmd = this.contextMenuDefinitions[i];
            if (id == cmd.ContextMenuID.toString()) {
                let link = this.resolve(cmd.Link);
                if (cmd.LinkMode) {
                    Navigation.openWindow(link, cmd.LinkMode);
                } else {
                    Navigation.navigate(link);
                }
                break;
            }
        }
    }

    /**
     * Set control value.
     * @param s Value to be set.
     * @hidden
     */
    public setValue(value: string): void {

    }

    /**
     * Get the control value.
     * @hidden
     */
    public getValue(): string {
        return null;
    }

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField?: string): ControlTemplate {
        let controlTemplate = new ControlTemplate();
        let elem = $("<a></a>");
        elem.html(this.textBlockService.get(this.controlDefinition.Caption, this.textBlockCategory));
        elem.addClass(CssClass.contextMenu);
        elem.attr(CONTROL_ID_ATTRIBUTE_NAME, this.controlDefinition.ControlID);
        controlTemplate.template = elem[0].outerHTML;
        controlTemplate.element = elem;
        return controlTemplate;
    }
    
    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
    }
}
